import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanDeactivate } from '@angular/router';
import { ConfirmationComponent } from '@components/confirmation/1.0/confirmation.component';
import { ComponentCanDeactivate } from '@extensions/component-can-deactivate';
import { TranslateService } from '@ngx-translate/core';
import { UnsavedChangesService } from '@services/unsaved-changes/1.0/unsaved-changes.service';
import { firstValueFrom, Observable, of, switchMap } from 'rxjs';

/**
 * Route Guard that prompts user for unsaved changes if the form is dirty and they try to navigate away
 */
@Injectable()
export class CanDeactivateGuard implements CanDeactivate<ComponentCanDeactivate> {
  constructor(
    private dialog: MatDialog,
    private translateService: TranslateService,
    private unsavedChangesService: UnsavedChangesService,
  ) {}

  canDeactivate(component: ComponentCanDeactivate | null | undefined): Observable<boolean> {
    if (!component) {
      return of(true);
    }

    if (!component.canDeactivate()) {
      const ref = this.dialog.open(ConfirmationComponent, {
        data: this.translateService.instant('app.unsaved'),
      });
      // afterClosed returns a boolean: true if they wish to proceed and false to stay on page
      return ref.afterClosed().pipe(
        switchMap((result) => {
          this.unsavedChangesService.setResult(result);
          return of(result ? true : false);
        }),
      );
    } else {
      return of(true);
    }
  }

  /**
   * Checks if a component can be deactivated.
   * Returns a Promise that resolves to true if navigation is allowed, false if cancelled.
   * This method can be called directly without going through router navigation.
   *
   * @param component - Component to check
   * @returns Promise<boolean> - true if navigation allowed, false if user cancelled
   */
  checkComponent(component: ComponentCanDeactivate): Promise<boolean> {
    return firstValueFrom(this.canDeactivate(component));
  }
}
